import { css } from '@emotion/react'
import { Flow, Heading, Status, Text, Theme, useTheme } from 'bold-ui'
import { useFlags } from 'config/useFlagsContext'
import { useMemo } from 'react'
import { isEmpty } from 'util/checks'

import { ConsultaSeguimentoResultadoView } from '../consulta-seguimento/consulta-seguimento-resultado/ConsultaSeguimentoResultadoView'
import { ConsultaSeguimentoViewModel } from '../consulta-seguimento/model-consultaSeguimento'
import { MChatResultadoView } from '../mchat/mchat-resultado/MChatResultadoView'
import { MChatRiscoPrevioRelevanteInfoModel, MChatViewModel } from '../mchat/model-mchat'
import { MCHAT_RF_ACCORDION_SECTIONS, MChatRfAccordionSectionEnum } from '../model-mChatRf'

export interface MChatRfAccordionSectionProps {
  section: MChatRfAccordionSectionEnum
  value: MChatViewModel | ConsultaSeguimentoViewModel | null
  stepper: React.ReactNode
  primaryActions?: React.ReactNode
  secondaryActions?: React.ReactNode
  alertaMChatAnterior?: MChatRiscoPrevioRelevanteInfoModel
  alertaConsultaSeguimentoAnterior?: MChatRiscoPrevioRelevanteInfoModel
  isConsultaSeguimentoAtiva?: boolean
  isRegistradoAgora?: boolean
  hideDivider?: boolean
  isConsultaSeguimentoDesatualizada?: boolean
}

export function MChatRfAccordionSection(props: Readonly<MChatRfAccordionSectionProps>) {
  const {
    section,
    value,
    stepper,
    primaryActions,
    secondaryActions,
    alertaMChatAnterior,
    alertaConsultaSeguimentoAnterior,
    isConsultaSeguimentoAtiva = false,
    isRegistradoAgora = false,
    hideDivider = false,
    isConsultaSeguimentoDesatualizada = false,
  } = props

  const { PILOTO_M_CHAT_RF_ENABLED } = useFlags()
  const theme = useTheme()

  const hasError = isConsultaSeguimentoDesatualizada

  const classes = useMemo(() => createStyles(theme, section, isConsultaSeguimentoAtiva, hideDivider), [
    hideDivider,
    theme,
    section,
    isConsultaSeguimentoAtiva,
  ])

  const { title, description } = MCHAT_RF_ACCORDION_SECTIONS(PILOTO_M_CHAT_RF_ENABLED)[section]
  const hasValues = !isEmpty(value)

  return (
    <Flow
      direction='horizontal'
      gap={0.625}
      justifyContent='flex-start'
      style={[classes.section, hasError && classes.sectionError]}
    >
      {stepper}

      <Flow direction='vertical' gap={0.5} style={classes.fullWidth}>
        <Flow direction='horizontal' justifyContent='space-between' style={classes.fullWidth}>
          <Flow direction='horizontal' alignItems='center' gap={1}>
            <Heading level={2} style={classes.textColor}>
              {title}
            </Heading>
            {isRegistradoAgora && <Status icon='clockOutline' type='info' text='Adicionado agora' />}
            {primaryActions}
          </Flow>

          {secondaryActions && (
            <Flow direction='horizontal' gap={0.5}>
              {secondaryActions}
            </Flow>
          )}
        </Flow>
        {!hasValues && <Text style={classes.textColor}>{description}</Text>}

        {hasValues && section === MChatRfAccordionSectionEnum.MCHAT && (
          <MChatResultadoView
            value={value as MChatViewModel}
            alertaMChatAnterior={alertaMChatAnterior}
            isRegistradoAgora={isRegistradoAgora}
          />
        )}

        {hasValues && section === MChatRfAccordionSectionEnum.CONSULTA_SEGUIMENTO && (
          <ConsultaSeguimentoResultadoView
            value={value as ConsultaSeguimentoViewModel}
            isConsultaSeguimentoDesatualizada={isConsultaSeguimentoDesatualizada}
            alertaConsultaSeguimentoAnterior={alertaConsultaSeguimentoAnterior}
          />
        )}
      </Flow>
    </Flow>
  )
}

const createStyles = (
  theme: Theme,
  section: MChatRfAccordionSectionEnum,
  isConsultaSeguimentoAtiva: boolean,
  hideDivider: boolean
) => ({
  fullWidth: css`
    width: 100%;
  `,
  section: css`
    padding: 1rem;
    background-color: ${section === MChatRfAccordionSectionEnum.CONSULTA_SEGUIMENTO &&
    !isConsultaSeguimentoAtiva &&
    theme.pallete.gray.c90};
    border-top: ${hideDivider ? 'none' : `1px solid ${theme.pallete.gray.c80}`};
    grid-auto-columns: 1.5rem 1fr;
    position: relative;
  `,
  sectionError: css`
    border: 1px solid ${theme.pallete.status.danger.c40};
    margin: 0 -1px;
    margin-bottom: -1px;
  `,
  textColor: css`
    color: ${section === MChatRfAccordionSectionEnum.MCHAT || isConsultaSeguimentoAtiva
      ? theme.pallete.gray.c10
      : theme.pallete.gray.c40};
  `,
})
